import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { useFormData } from '../../hooks/useFormData';
import {
  chainValidations,
  objectToString,
  stringToObject,
  groupValidation,
} from '../../utils/form-utils';
import GradeInputColumn from './DateInputField/GradeInputColumn';
import { dateIsEmpty } from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

export const getColumns = () => [
  'ApplyNPS',
  'ApplyResponses',
  'InterviewNPS',
  'InterviewResponses',
  'ReadyToHireNPS',
  'ReadyToHireResponses',
];
export const getRows = () => ['scs1', 'scs2', 'scs3', 'scs4'];
export const getKeys = () => getRows().flatMap((r) => getColumns().map((c) => `${r}${c}`));

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

export function CoreNpsSCSInput({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);
  const { data } = useFormData();

  useEffect(() => {
    if (getValues(question.name) === undefined) setValue(question.name, '');
  }, [getValues, question.name, setValue]);

  const aId = useId();
  const makeId = (s: string) => `${anchorId ?? aId}-${s}`;

  const initialValues = useMemo(
    () => stringToObject(getValues(question.name)),
    [getValues, question.name],
  );
  const [values, setValues] = useState(initialValues);

  const { onChange } = register(question.name, {
    validate: (value: string) =>
      chainValidations([
        groupValidation({
          value: stringToObject(value),
          answers: data?.answers,
          question,
          sumColumns: [],
        }),
      ]),
  });

  const headerLabel = (col: string) => {
    switch (col) {
      case 'ApplyNPS':
        return { stage: 'Apply stage', detail: 'Overall Net Promoter Score' };
      case 'ApplyResponses':
        return { stage: 'Apply stage', detail: 'Number of survey responses' };
      case 'InterviewNPS':
        return { stage: 'Interview stage', detail: 'Overall Net Promoter Score' };
      case 'InterviewResponses':
        return { stage: 'Interview stage', detail: 'Number of survey responses' };
      case 'ReadyToHireNPS':
        return { stage: 'Ready to hire stage', detail: 'Overall Net Promoter Score' };
      case 'ReadyToHireResponses':
        return { stage: 'Ready to hire stage', detail: 'Number of survey responses' };
      default:
        return { stage: '', detail: col };
    }
  };

  const groupLabel = (idx: number) => {
    if (idx <= 1) return 'Apply stage';
    if (idx <= 3) return 'Interview stage';
    return 'Ready to hire stage';
  };

  const labelForRow = (row: string) => row.toUpperCase();

  const triggerChange = (updated: typeof values) => {
    const asString = objectToString(updated);
    onChange({ target: { name: question.name, value: dateIsEmpty(asString) ? '' : asString } });
  };

  const handleChange = (field: keyof typeof values) => (val: string) => {
    const updated = { ...values, [field]: val };
    setValues(updated);
    triggerChange(updated);
  };

  const dividerStyle: React.CSSProperties = {
    borderRight: '2px solid #b1b4b6',
    paddingLeft: '8px',
    paddingRight: '8px',
  };
  const cellStyle: React.CSSProperties = {
    paddingLeft: '8px',
    paddingRight: '8px',
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <table className="govuk-table sticky-header-table sticky-header-table--double">
        <style>{`
          .sticky-header-table .govuk-table__head .govuk-table__header {
            position: sticky;
            top: 0;
            background: #ffffff;
            z-index: 2;
          }
          .sticky-header-table--double .govuk-table__head tr:nth-child(1) .govuk-table__header {
            top: 0;
          }
          .sticky-header-table--double .govuk-table__head tr:nth-child(2) .govuk-table__header {
            top: 2.5rem;
          }
        `}</style>
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header" style={cellStyle}></th>
            <th colSpan={2} scope="col" className="govuk-table__header" style={dividerStyle}>
              {groupLabel(0)}
            </th>
            <th colSpan={2} scope="col" className="govuk-table__header" style={dividerStyle}>
              {groupLabel(2)}
            </th>
            <th colSpan={2} scope="col" className="govuk-table__header" style={cellStyle}>
              {groupLabel(4)}
            </th>
          </tr>
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header" style={cellStyle}>
              Grade
            </th>
            {getColumns().map((col, idx) => {
              const isDivider = (idx + 1) % 2 === 0; // every 2 columns
              return (
                <th
                  key={col}
                  scope="col"
                  className="govuk-table__header"
                  style={isDivider ? dividerStyle : cellStyle}
                >
                  {headerLabel(col).detail}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {getRows().map((row) => (
            <tr key={row} className="govuk-table__row">
              <th scope="row" className="govuk-table__header" style={cellStyle}>
                {labelForRow(row)}
              </th>
              {getColumns().map((col, idx) => {
                const isDivider = (idx + 1) % 2 === 0;
                const field = `${row}${col}` as keyof typeof values;
                return (
                  <td
                    key={String(field)}
                    className="govuk-table__cell"
                    style={isDivider ? dividerStyle : cellStyle}
                  >
                    <GradeInputColumn
                      testId={`${String(field)}-${labelForRow(row).toLowerCase()}`}
                      disabled={disabled}
                      allowFloat={!!question.rules?.float}
                      anchorId={makeId(String(field))}
                      onChange={handleChange(field)}
                      value={values[field] ?? ''}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
